// Only the i18n + platformInject surface of @polaris-platform/core is imported here.
// Do NOT import from '@polaris-platform/core/routing' or '/analytics': those submodules
// pull in `single-spa` (a required peer that is NOT installed or declared as an app
// dependency), which the single-file bundler would then fail to resolve. If those
// modules are ever needed, mark `single-spa` as external in tools/build-single-bundle.mjs
// and let the Polaris shell provide it - never bundle it into the dotcom artifact.
import { platformInject } from '@polaris-platform/core';
import {
  PlatformLocaleService,
  PLATFORM_FALLBACK_LOCALE_CODE,
  formatLocaleCode,
  parseLocaleCode,
  type LocaleCode,
} from '@polaris-platform/core/i18n';

/**
 * Resolves the active locale from the POLARIS platform.
 *
 * In POLARIS shell mode the shell owns the locale: the language switcher lives in
 * the shell header, the locale is encoded in the URL path (`/{lang}-{country}/`),
 * mirrored to the `fdx_redirect` / `fdx_locale` cookies, and a full page reload is
 * triggered on language switch (there is no runtime locale-change event contract).
 * `PlatformLocaleService.getActiveLocale()` applies the platform's canonical
 * resolution order:
 *
 *   URL pathname -> fdx_redirect cookie -> fdx_locale cookie -> fallback (en-us)
 *
 * We consume that service so the MFE and the shell never disagree on the locale.
 * The `try/catch` keeps the app resilient if the platform DI context is ever
 * unavailable, mirroring the same URL-first resolution as a last resort.
 */
export async function getPolarisLocaleCode(): Promise<LocaleCode> {
  try {
    return await platformInject(PlatformLocaleService).getActiveLocale();
  } catch {
    const localeFromUrl = window.location.pathname.match(/\/(([a-z]{2})-([a-z]{2}))\//i)?.[1];
    return parseLocaleCode(localeFromUrl) ?? PLATFORM_FALLBACK_LOCALE_CODE;
  }
}

/**
 * Formats a `LocaleCode` as an ISO locale string, e.g. `{ en, us } -> "en-us"`.
 * This matches the hyphen-lowercase form LocaleService.update() already receives
 * from `extractLocaleFromLocation`, which Locale.toLocale() parses into the posix
 * value the ngx-translate pipeline consumes.
 */
export function toIsoLocale(locale: LocaleCode): string {
  return formatLocaleCode(locale);
}
